import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { ExternalExamsRegistrationsSideBarFilter } from './side-bar-filter/external-exams-registrations-side-bar-filter';
import { getExternalExamRegistrationsOperation } from './operations/get-external-exam-registrations-operation';
import { deleteExternalExamRegistrationOperation } from './operations/delete-external-exam-registration-operation';
import { updatePaymentExemptedOperation } from './operations/update-payment-exempted-operation';
import {
  updateRegistrationGradesOperation,
  UpdateRegistrationGradesRequest,
} from './operations/update-registration-grades-operation';
import { registerExternalExamOperation } from './operations/register-external-exam-operation';
import {
  EligibilityStatus,
  ExternalExamRegistrationStudentDetails,
  RegistrationSourceType,
} from '../../../../api/types/external-exams/registration/external-exam-student-registration-eligibility';
import { EditRegistrationGradesDialog } from './components/edit-registration-grades-dialog';
import { AddEligibilityDialog } from './add-student-eligibility/add-eligibility-dialog';
import externalExamGroupsStore from '../../../../stores/external-exam-groups-store';
import dateUtils from '../../../../utils/date-format';
import { UserPageDialog } from '../../../../component/ui/search-users/user-page-dialog/user-page-dialog';
import { UserType } from '../../../../api/types/user/user-type';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';

export const ExternalExamsRegistrationsTab = observer(() => {
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState<
    ExternalExamRegistrationStudentDetails[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
  const [deleteRegistrationId, setDeleteRegistrationId] = useState<
    number | null
  >(null);
  const [updatePaymentExemptedId, setUpdatePaymentExemptedId] = useState<
    number | null
  >(null);
  const [updateGradesId, setUpdateGradesId] = useState<number | null>(null);
  const [editGradesRegistration, setEditGradesRegistration] =
    useState<ExternalExamRegistrationStudentDetails | null>(null);
  const [showAddEligibilityDialog, setShowAddEligibilityDialog] =
    useState<boolean>(false);
  const [selectedUserForDialog, setSelectedUserForDialog] = useState<
    UserSummaryType | undefined
  >();
  const [registeringExamId, setRegisteringExamId] = useState<number | null>(
    null,
  );
  const [deleteRegistration, setDeleteRegistration] =
    useState<ExternalExamRegistrationStudentDetails | null>(null);
  const [registrationForWarning, setRegistrationForWarning] =
    useState<ExternalExamRegistrationStudentDetails | null>(null);

  const handleSearch = async (
    groupId: number,
    examId: number,
    branchIds: number[],
  ) => {
    setSelectedGroupId(groupId);
    setSelectedExamId(examId);
    setSelectedBranchIds(branchIds);
    await getExternalExamRegistrationsOperation(
      groupId,
      examId,
      branchIds,
      setRegistrations,
      setLoading,
    );
  };

  const handleDeleteRegistration = async (
    registrationId: number,
    groupId: number,
    examId: number,
  ) => {
    await deleteExternalExamRegistrationOperation(
      groupId,
      examId,
      registrationId,
      setDeleteRegistrationId,
      async () => {
        // Refresh the registrations list after successful deletion
        if (groupId && examId) {
          await getExternalExamRegistrationsOperation(
            groupId,
            examId,
            selectedBranchIds,
            setRegistrations,
            setLoading,
          );
        }
      },
    );
  };

  const handleUpdatePaymentExempted = async (
    registrationId: number,
    groupId: number,
    examId: number,
    paymentExempted: boolean,
  ) => {
    await updatePaymentExemptedOperation(
      groupId,
      examId,
      registrationId,
      paymentExempted,
      setUpdatePaymentExemptedId,
      async () => {
        // Refresh the registrations list after successful update
        if (groupId && examId) {
          await getExternalExamRegistrationsOperation(
            groupId,
            examId,
            selectedBranchIds,
            setRegistrations,
            setLoading,
          );
        }
      },
    );
  };

  const handleUpdateGrades = async (
    request: UpdateRegistrationGradesRequest,
  ) => {
    if (!editGradesRegistration || !selectedGroupId || !selectedExamId) {
      return;
    }

    await updateRegistrationGradesOperation(
      selectedGroupId,
      selectedExamId,
      editGradesRegistration.id,
      request,
      setUpdateGradesId,
      async () => {
        // Refresh the registrations list after successful update
        if (selectedGroupId && selectedExamId) {
          await getExternalExamRegistrationsOperation(
            selectedGroupId,
            selectedExamId,
            selectedBranchIds,
            setRegistrations,
            setLoading,
          );
          setEditGradesRegistration(null);
        }
      },
    );
  };

  const handleRegisterExam = async (
    registration: ExternalExamRegistrationStudentDetails,
  ) => {
    if (!selectedGroupId || !selectedExamId) {
      return;
    }

    await registerExternalExamOperation(
      selectedGroupId,
      selectedExamId,
      registration.id,
      setRegisteringExamId,
      async () => {
        // Refresh the registrations list after successful registration
        if (selectedGroupId && selectedExamId) {
          await getExternalExamRegistrationsOperation(
            selectedGroupId,
            selectedExamId,
            selectedBranchIds,
            setRegistrations,
            setLoading,
          );
        }
      },
    );
  };

  // Get selected group and exam details for tooltip
  const selectedGroup = externalExamGroupsStore.groups.find(
    (g) => g.id === selectedGroupId,
  );
  const selectedExam = selectedGroup?.exams.find(
    (e) => e.id === selectedExamId,
  );

  const getTooltipText = () => {
    if (!selectedGroupId || !selectedExamId) {
      return 'יש לבחור מקבץ ומבחן תחילה';
    }

    if (!selectedGroup || !selectedExam) {
      return 'הוספת זכאות להרשמה';
    }

    const examDate = dateUtils.displayFormatDate(selectedExam.startAt);
    return `הוספת זכאות להרשמה - מקבץ: ${selectedGroup.groupName}, מועד: ${selectedExam.abstractExternalExam.examName} (${examDate})`;
  };

  const handleUserClick = (
    registration: ExternalExamRegistrationStudentDetails,
  ) => {
    setSelectedUserForDialog(registration.userDetails);
  };

  const closeUserDialog = () => {
    setSelectedUserForDialog(undefined);
  };

  const handleExportToExcel = () => {
    // Helper function to get eligibility status text
    const getEligibilityStatusText = (status: EligibilityStatus): string => {
      switch (status) {
        case EligibilityStatus.REGISTER:
          return 'רשום';
        case EligibilityStatus.ELIGIBILITY:
          return 'זכאי';
        case EligibilityStatus.CANCELED:
          return 'בוטל';
        default:
          return '-';
      }
    };

    // Helper function to get registration source text
    const getRegistrationSourceText = (
      sourceType: RegistrationSourceType | null,
    ): string => {
      if (sourceType === RegistrationSourceType.SELF) {
        return 'רישום עצמאי';
      }
      if (sourceType === RegistrationSourceType.ADMIN) {
        return 'ע״י מנהל';
      }
      return 'טרם נרשם';
    };

    // Helper function to format date
    const formatDate = (date: Date | string | null | undefined): string => {
      if (!date) return '';
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateUtils.displayFormatDate(dateObj);
    };

    // Helper function to format boolean
    const formatBoolean = (value: boolean | null): string => {
      if (value === null) return '';
      return value ? 'כן' : 'לא';
    };

    // Create header row
    const headers = [
      'ת.ז',
      'שם פרטי',
      'שם משפחה',
      'טלפון נייד',
      'מייל',
      'תאריך לידה',
      'סטטוס הרשמה',
      'קוד פעולה',
      'מי רשם',
      'פטור מתשלום',
      'נכח',
      'ציון מגן',
      'ציון בחינה',
      'ציון סופי',
    ];

    // Create data rows
    const dataRows = registrations.map((registration) => [
      registration.userDetails.nationalId || '',
      registration.userDetails.firstName || '',
      registration.userDetails.lastName || '',
      registration.userDetails.phoneNumber || '',
      registration.userDetails.email || '',
      formatDate(registration.userDetails.dateOfBirth),
      getEligibilityStatusText(registration.eligibilityStatus),
      registration.actionNumber || '',
      getRegistrationSourceText(registration.registrationSourceType),
      formatBoolean(registration.paymentExempted),
      formatBoolean(registration.attended),
      registration.internalGrade?.toString() || '',
      registration.externalGrade?.toString() || '',
      registration.finalGrade?.toString() || '',
    ]);

    // Combine headers and data
    const excelData = [headers, ...dataRows];

    // Generate and download
    generateAndDownloadExcelFile(excelData);
  };

  return (
    <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
      <div
        style={{
          height: 'calc(100vh - 230px)',
          minHeight: '100%',
          width: '300px',
          minWidth: '250px',
        }}
      >
        <ExternalExamsRegistrationsSideBarFilter onSearch={handleSearch} />
      </div>

      <div style={{ flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
            gap: '16px',
          }}
        >
          <Button
            onClick={() => setShowAddEligibilityDialog(true)}
            style={{ height: '30px' }}
            label={'הוספת זכאות להרשמה'}
            icon="pi pi-plus"
            disabled={!selectedGroupId || !selectedExamId}
            tooltip={getTooltipText()}
            tooltipOptions={{ showOnDisabled: true }}
          />
          <Button
            style={{
              height: '30px',
              width: '30px',
              backgroundColor: '#10b981',
              borderColor: '#10b981',
              marginLeft: '20px',
            }}
            icon="pi pi-file-excel"
            tooltip="ייצוא לאקסל"
            onClick={handleExportToExcel}
            disabled={registrations.length === 0}
          />
        </div>
        <DataTable
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו רישומים</div>
          }
          value={registrations}
          dataKey="id"
          loading={loading}
          onRowClick={(e: DataTableRowClickEvent) => {
            handleUserClick(e.data as ExternalExamRegistrationStudentDetails);
          }}
          tableStyle={{
            direction: 'rtl',
            cursor: 'pointer',
          }}
        >
          <Column
            style={{ textAlign: 'right' }}
            header="שם משתמש"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>
                {rowData.userDetails.firstName} {rowData.userDetails.lastName}
              </div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="ת.ז."
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{rowData.userDetails.nationalId}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="סטטוס הרשמה"
            body={(rowData: ExternalExamRegistrationStudentDetails) => {
              const getStatusDisplay = () => {
                switch (rowData.eligibilityStatus) {
                  case EligibilityStatus.REGISTER:
                    return { label: 'נרשם', severity: 'success' as const };
                  case EligibilityStatus.ELIGIBILITY:
                    return { label: 'זכאות', severity: 'warning' as const };
                  case EligibilityStatus.CANCELED:
                    return { label: 'בוטל', severity: 'danger' as const };
                  default:
                    return { label: 'לא ידוע', severity: 'secondary' as const };
                }
              };
              const status = getStatusDisplay();
              return <Tag severity={status.severity} value={status.label} />;
            }}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="חיווי הרשמה"
            body={(rowData: ExternalExamRegistrationStudentDetails) => {
              if (
                rowData.registrationSourceType === RegistrationSourceType.SELF
              ) {
                return <div>הרשמה עצמית</div>;
              }
              if (
                rowData.registrationSourceType === RegistrationSourceType.ADMIN
              ) {
                return <div>הרשמה ע״י מנהל</div>;
              }
              if (rowData.registrationSourceType === null) {
                return <div>טרם נרשם</div>;
              }
              return <div>-</div>;
            }}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="קוד פעולה"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{rowData.actionNumber ?? '-'}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="הרשמה"
            body={(rowData: ExternalExamRegistrationStudentDetails) => {
              if (rowData.eligibilityStatus === EligibilityStatus.REGISTER) {
                return <div>-</div>;
              }

              return (
                <Button
                  label="הרשמה"
                  icon="pi pi-check"
                  onClick={() => setRegistrationForWarning(rowData)}
                  loading={registeringExamId === rowData.id}
                  disabled={
                    registeringExamId !== null ||
                    !selectedGroupId ||
                    !selectedExamId
                  }
                  style={{ height: '30px', fontSize: '14px' }}
                />
              );
            }}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="פטור מתשלום"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <Checkbox
                checked={rowData.paymentExempted}
                onChange={(e) => {
                  if (selectedGroupId && selectedExamId) {
                    handleUpdatePaymentExempted(
                      rowData.id,
                      selectedGroupId,
                      selectedExamId,
                      e.checked ?? false,
                    );
                  }
                }}
                disabled={
                  updatePaymentExemptedId !== null ||
                  !selectedGroupId ||
                  !selectedExamId
                }
              />
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="נכח"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>
                {rowData.attended === null
                  ? '-'
                  : rowData.attended
                    ? 'כן'
                    : 'לא'}
              </div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="ציון מגן"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{rowData.internalGrade ?? '-'}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="ציון חיצוני"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{rowData.externalGrade ?? '-'}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="ציון סופי"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{rowData.finalGrade ?? '-'}</div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            header="נוצר בתאריך"
            body={(rowData: ExternalExamRegistrationStudentDetails) => (
              <div>{dateUtils.displayFormatDate(rowData.createdAt)}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            header="פעולות"
            body={(rowData: ExternalExamRegistrationStudentDetails) => {
              const isDeleteDisabled =
                rowData.eligibilityStatus === EligibilityStatus.REGISTER &&
                rowData.paymentExempted;
              return (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    icon="pi pi-pencil"
                    text
                    severity="warning"
                    tooltip={'עריכת נוכחות וציונים'}
                    loading={updateGradesId === rowData.id}
                    disabled={
                      updateGradesId !== null ||
                      !selectedGroupId ||
                      !selectedExamId
                    }
                    onClick={() => {
                      setEditGradesRegistration(rowData);
                    }}
                  />
                  <Button
                    icon="pi pi-trash"
                    text
                    severity="danger"
                    tooltip={'הסרת זכאות להרשמה'}
                    loading={deleteRegistrationId === rowData.id}
                    disabled={
                      isDeleteDisabled ||
                      deleteRegistrationId !== null ||
                      !selectedGroupId ||
                      !selectedExamId
                    }
                    onClick={() => {
                      setDeleteRegistration(rowData);
                    }}
                  />
                </div>
              );
            }}
          />
        </DataTable>
      </div>

      {editGradesRegistration && (
        <EditRegistrationGradesDialog
          visible={editGradesRegistration !== null}
          onHide={() => setEditGradesRegistration(null)}
          registration={editGradesRegistration}
          onApprove={handleUpdateGrades}
          loading={updateGradesId !== null}
        />
      )}

      {showAddEligibilityDialog && (
        <AddEligibilityDialog
          visible={showAddEligibilityDialog}
          onHide={() => setShowAddEligibilityDialog(false)}
          groupId={selectedGroupId}
          examId={selectedExamId}
          onSuccess={async () => {
            setShowAddEligibilityDialog(false);
            // Refresh the registrations list with the current filter state
            if (selectedGroupId && selectedExamId) {
              await getExternalExamRegistrationsOperation(
                selectedGroupId,
                selectedExamId,
                selectedBranchIds,
                setRegistrations,
                setLoading,
              );
            }
          }}
        />
      )}

      {deleteRegistration && (
        <WarningDialog
          header="אישור הסרת זכאות להרשמה"
          isVisible={deleteRegistration !== null}
          onHide={() => {
            if (deleteRegistrationId === null) {
              setDeleteRegistration(null);
            }
          }}
          addIcon={true}
          content="האם אתה בטוח שברצונך להסיר את הזכאות להרשמה?"
          quoteName={`${deleteRegistration.userDetails.firstName} ${deleteRegistration.userDetails.lastName}`}
          dangerousContent={
            deleteRegistration.eligibilityStatus ===
              EligibilityStatus.REGISTER && deleteRegistration.paymentExempted
              ? 'סטודנט זה רשום למבחן ופטור מתשלום. הסרת הזכאות תמחק את הרישום.'
              : deleteRegistration.eligibilityStatus ===
                  EligibilityStatus.REGISTER
                ? 'סטודנט זה רשום למבחן. הסרת הזכאות תמחק את הרישום.'
                : undefined
          }
          smallLetters="פעולה זו אינה ניתנת לביטול"
          onConfirm={async () => {
            if (deleteRegistration && selectedGroupId && selectedExamId) {
              await handleDeleteRegistration(
                deleteRegistration.id,
                selectedGroupId,
                selectedExamId,
              );
              setDeleteRegistration(null);
            }
          }}
          confirmLabel="הסר"
          confirmLoading={deleteRegistrationId !== null}
        />
      )}

      {registrationForWarning && (
        <WarningDialog
          header="אישור הרשמה למבחן"
          isVisible={registrationForWarning !== null}
          onHide={() => {
            if (registeringExamId === null) {
              setRegistrationForWarning(null);
            }
          }}
          addIcon={true}
          content="ההרשמה כרוכה בשירות תשלום"
          notInformation="אם אינך לחצת על כפתור זה בעבר המערכת תיצור עבורך חוב, לאחר היצצרות החוב ניתן יהיה לשלם אותו ולאחר מכן יש ללחוץ על כפתור זה שוב על מנת להשלים את ההרשמה"
          smallLetters="אם התשלום עבור מבחן זה שולם, ניתן להתעלם מהודעה זו"
          onConfirm={async () => {
            if (registrationForWarning) {
              await handleRegisterExam(registrationForWarning);
              setRegistrationForWarning(null);
            }
          }}
          confirmLabel="המשך להרשמה"
          confirmLoading={registeringExamId !== null}
        />
      )}

      <UserPageDialog
        userRole={UserType.STUDENT}
        selectedUser={selectedUserForDialog}
        setSelectedUser={setSelectedUserForDialog}
        closeDialog={closeUserDialog}
        onHide={closeUserDialog}
      />
    </div>
  );
});
